import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import MapView from "../../maps/MapView";

const truncate = (text, maxLength = 60) =>
  text?.length > maxLength ? text.slice(0, maxLength) + "..." : text;

const IssuesTable = ({ issues }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate()

  const issuesPerPage = 5;

  // Unique filter options
  const uniqueStatuses = [...new Set(issues.map((i) => i.status))];
  const uniqueCategories = [...new Set(issues.map((i) => i.category))];

  useEffect(() => {
    let temp = issues;

    if (statusFilter) {
      temp = temp.filter((i) => i.status === statusFilter);
    }

    if (categoryFilter) {
      temp = temp.filter((i) => i.category === categoryFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      temp = temp.filter(
        (i) =>
          i.location.toLowerCase().includes(term) ||
          i.issue_id.toLowerCase().includes(term) ||
          i.category.toLowerCase().includes(term) ||
          i.solution?.assigned_to?.name?.toLowerCase().includes(term)
      );
    }

    setFilteredIssues(temp);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter, issues]);

  const indexOfLast = currentPage * issuesPerPage;
  const indexOfFirst = indexOfLast - issuesPerPage;
  const currentIssues = filteredIssues.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredIssues.length / issuesPerPage);

  const paginate = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <motion.div
      className="container mt-4"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-4 mb-2">
          <input
            type="search"
            className="form-control"
            placeholder="Search issues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="col-md-4 mb-2">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Filter by Status</option>
            {uniqueStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-2">
          <select
            className="form-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Filter by Category</option>
            {uniqueCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {Array.isArray(filteredIssues) && filteredIssues.length === 0 ? (
        <div className="alert alert-info">No issues found.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle shadow-sm">
              <thead className="table-success text-center">
                <tr>
                  <th>#</th>
                  <th>Issue ID</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Recurrent</th>
                  <th>Location</th>
                  <th>Assigned To</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {currentIssues.map((issue, i) => (
                  <tr align='center' key={issue.issue_id}
                    onClick={() => navigate(`/issues/${issue.issue_id}`)}
                   style={{ cursor: "pointer" }}>
                    <td>{indexOfFirst + i + 1}</td>
                    <td>{issue.issue_id}</td>
                    <td>{issue.category}</td>
                    <td>
                    <span
                        className={`badge p-2 py-1 ${
                          issue.status === "Pending"
                            ? "bg-warning text-dark"
                            : issue.status === "Resolved"
                            ? "bg-success"
                            : "bg-info"
                        }`}
                      >
                        {issue.status}
                      </span>
                    </td>
                    <td>
                        <span
                        className={`badge p-2 py-1 ${
                          issue.is_recurrent
                            ? "bg-danger"
                            : "bg-success"
                        }`}
                      >
                        {issue.is_recurrent ? "Yes" : "No"}
                      </span></td>
                    <td title={issue.location}>{truncate(issue.location)}</td>
                    <td>{issue.solution?.assigned_to?.name || "————"}</td>
                    <td>{new Date(issue.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <nav className="d-flex justify-content-center mt-3">
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 && "disabled"}`}>
                <button className="page-link" onClick={() => paginate(currentPage - 1)}>
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${currentPage === i + 1 && "active"}`}>
                  <button className="page-link" onClick={() => paginate(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}

              <li className={`page-item ${currentPage === totalPages && "disabled"}`}>
                <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                  Next
                </button>
              </li>
            </ul>
          </nav>

          {/* map  */}
          <motion.div
            className="mt-5"
            style={{ maxWidth: "100%", overflowX: "auto" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div
              className="mx-auto shadow rounded p-3"
              style={{
                // maxHeight: "350px",
                width: "100%",
                // maxWidth: "",
                border: "1px solid #ced4da",
                background: "#ffffff",
              }}
            >
              <h5 className="text-center text-success mb-3 fw-semibold">
                Issue Map Overview
              </h5>
              <div style={{height:'50vh'}}>
                <MapView coordinates={filteredIssues} />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

export default IssuesTable;
